import { useEffect, useRef, useState } from "react";
import { api, formatApiError, resolveMediaUrl, postCategories } from "../lib/api";
import slugify from "slugify";
import { Plus, PencilSimple, Trash, FloppyDisk, X, UploadSimple, Image as ImageIcon, PlusCircle, Check, DownloadSimple, Envelope, User, NewspaperClipping, Microphone, Play, ChatCircle } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const EMPTY = {
  type: "article",
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  cover_image: "",
  media_url: "",
  gallery: "",
  categories: [],
  tags: "",
  featured: false,
  author: "",
  duration: "",
};

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // post or null
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/posts", { params: { limit: 200 } }).then((r) => setPosts(r.data));

  useEffect(() => { load(); }, []);

  const startNew = (presetType = "article") => {
    setEditing({ _new: true });
    setForm({ ...EMPTY, type: presetType, author: user?.name || "Baseline Breakdown Editorial" });
    setError("");
  };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      categories: postCategories(p),
      tags: (p.tags || []).join(", "),
      gallery: (p.gallery || []).join("\n"),
    });
    setError("");
  };
  const close = () => { setEditing(null); setError(""); };

  const setField = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "title" && (!f.slug || editing?._new)) {
        next.slug = slugify(v, { lower: true, strict: true });
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true); setError("");
    const payload = {
      ...form,
      categories: Array.isArray(form.categories) ? form.categories.filter(Boolean) : [],
      tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags,
      gallery: typeof form.gallery === "string" ? form.gallery.split("\n").map((t) => t.trim()).filter(Boolean) : (form.gallery || []),
    };
    try {
      if (editing?._new) await api.post("/posts", payload);
      else await api.put(`/posts/${editing.id}`, payload);
      await load();
      close();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/posts/${p.id}`);
    load();
  };

  const shown = posts.filter((p) => filter === "all" || p.type === filter);

  return (
    <div data-testid="admin-page" className="mx-auto max-w-[1400px] px-6 py-12 lg:px-12">
      <div className="flex items-end justify-between border-b border-white/10 pb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4FF00]">Editorial Desk</div>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tighter text-white md:text-5xl">Admin Dashboard</h1>
          <p className="mt-2 text-sm text-white/60">Signed in as {user?.email}</p>
        </div>
        {tab === "posts" && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => startNew("article")}
              data-testid="admin-new-article"
              className="flex items-center gap-2 bg-[#D4FF00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black hover:-translate-y-0.5"
            >
              <NewspaperClipping size={14} weight="bold" /> New Article
            </button>
            <button
              onClick={() => startNew("podcast")}
              data-testid="admin-new-podcast"
              className="flex items-center gap-2 border border-[#E36A53] bg-[#E36A53]/10 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-[#E36A53] hover:bg-[#E36A53] hover:text-black"
            >
              <Microphone size={14} weight="bold" /> New Podcast
            </button>
            <button
              onClick={() => startNew("digital")}
              data-testid="admin-new-digital"
              className="flex items-center gap-2 border border-[#4FB7D7] bg-[#4FB7D7]/10 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-[#4FB7D7] hover:bg-[#4FB7D7] hover:text-black"
            >
              <Play size={14} weight="bold" /> New Digital
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-1 border-b border-white/10">
        {[
          { key: "posts", label: "Posts" },
          { key: "subscribers", label: "Subscribers" },
          { key: "messages", label: "Messages" },
          { key: "account", label: "Account" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`relative px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
              tab === t.key ? "text-[#D4FF00]" : "text-white/60 hover:text-white"
            }`}
          >
            {t.label}
            {tab === t.key && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-[#D4FF00]" />}
          </button>
        ))}
      </div>

      {tab === "posts" && (
        <>
      <div className="mt-8 flex flex-wrap gap-2">
        {["all", "article", "podcast", "digital"].map((t) => (
          <button
            key={t}
            data-testid={`filter-${t}`}
            onClick={() => setFilter(t)}
            className={`border px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
              filter === t ? "border-[#D4FF00] bg-[#D4FF00] text-black" : "border-white/15 text-white/70 hover:border-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-8 border border-white/10">
        <div className="hidden grid-cols-[80px_1fr_120px_120px_100px_120px] gap-4 border-b border-white/10 bg-black px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 md:grid">
          <div>Type</div><div>Title</div><div>Category</div><div>Author</div><div>Featured</div><div>Actions</div>
        </div>
        {shown.map((p) => (
          <div key={p.id} data-testid={`admin-row-${p.slug}`} className="grid grid-cols-1 gap-2 border-b border-white/10 px-4 py-4 text-sm md:grid-cols-[80px_1fr_120px_120px_100px_120px] md:items-center md:gap-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00]">{p.type}</div>
            <div className="font-display text-base text-white">{p.title}</div>
            <div className="text-xs text-white/60">{postCategories(p).join(" · ")}</div>
            <div className="text-xs text-white/60">{p.author}</div>
            <div className="text-xs text-white/60">{p.featured ? "Yes" : "—"}</div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(p)} data-testid={`edit-${p.slug}`} className="border border-white/15 p-2 hover:border-[#D4FF00] hover:text-[#D4FF00]"><PencilSimple size={14} /></button>
              <button onClick={() => remove(p)} data-testid={`delete-${p.slug}`} className="border border-white/15 p-2 hover:border-red-500 hover:text-red-400"><Trash size={14} /></button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="px-4 py-12 text-center text-xs uppercase tracking-[0.2em] text-white/40">No posts.</div>}
      </div>
        </>
      )}

      {tab === "subscribers" && <SubscribersPanel />}
      {tab === "messages" && <MessagesPanel />}
      {tab === "account" && <AccountPanel />}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-3 backdrop-blur-sm sm:p-6">
          <div className="w-full max-w-3xl border border-white/10 bg-[#0a0a0a] p-5 sm:p-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4FF00]">{editing._new ? "Create" : "Edit"}</div>
                <h2 className="mt-1 font-display text-3xl font-bold tracking-tighter text-white">{editing._new ? "New Post" : form.title}</h2>
              </div>
              <button onClick={close} data-testid="editor-close" className="border border-white/15 p-2 hover:border-white"><X size={16} /></button>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <Field label="Type">
                <select data-testid="editor-type" value={form.type} onChange={(e) => setField("type", e.target.value)} className="w-full border border-white/15 bg-black px-3 py-2 text-white outline-none focus:border-[#D4FF00]">
                  <option value="article">Article</option>
                  <option value="podcast">Podcast</option>
                  <option value="digital">Digital</option>
                </select>
              </Field>
              <Field label="Category">
                <CategoryMultiSelect value={form.categories} onChange={(v) => setField("categories", v)} />
              </Field>
              <Field label="Title" full>
                <input data-testid="editor-title" value={form.title} onChange={(e) => setField("title", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Slug" full>
                <input data-testid="editor-slug" value={form.slug} onChange={(e) => setField("slug", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Cover Image" full>
                <CoverImageField value={form.cover_image} onChange={(v) => setField("cover_image", v)} />
              </Field>
              <Field label={form.type === "podcast" ? "Audio file URL (the episode)" : form.type === "digital" ? "Video / embed URL" : "Media URL (audio for podcast, video embed for digital)"} full>
                <MediaUrlField type={form.type} value={form.media_url} onChange={(v) => setField("media_url", v)} />
              </Field>
              <div className="md:col-span-2">
                <GalleryEditor value={form.gallery} onChange={(v) => setField("gallery", v)} />
              </div>
              <Field label="Author">
                <input data-testid="editor-author" value={form.author} onChange={(e) => setField("author", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Duration">
                <input data-testid="editor-duration" value={form.duration} onChange={(e) => setField("duration", e.target.value)} className={inputCls} placeholder="5 min read / 42 min" />
              </Field>
              <Field label="Tags (comma separated)" full>
                <input data-testid="editor-tags" value={form.tags} onChange={(e) => setField("tags", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Excerpt" full>
                <textarea data-testid="editor-excerpt" value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} rows={2} className={inputCls} />
              </Field>
              <Field label="Body" full>
                <BodyEditor value={form.body} onChange={(v) => setField("body", v)} />
                <div className="mt-2 text-[10px] leading-relaxed text-white/50">
                  <span className="font-bold uppercase tracking-[0.2em] text-[#D4FF00]">Tip:</span>{" "}
                  Click <span className="text-white">+ Insert embed</span> to drop an Instagram, TikTok, YouTube,
                  Vimeo, X/Twitter link — or an image / video URL — at your cursor position. It will render as an
                  inline embed in the published article.
                </div>
              </Field>
              <label className="col-span-full flex items-center gap-3 text-sm text-white/80">
                <input data-testid="editor-featured" type="checkbox" checked={!!form.featured} onChange={(e) => setField("featured", e.target.checked)} className="h-4 w-4" />
                Featured on homepage
              </label>
            </div>

            {error && <div data-testid="editor-error" className="mt-4 text-xs uppercase tracking-[0.2em] text-red-400">{error}</div>}

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={close} className="border border-white/15 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:border-white">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="editor-save" className="flex items-center gap-2 bg-[#D4FF00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black hover:-translate-y-0.5 disabled:opacity-50">
                <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full border border-white/15 bg-black px-3 py-2 text-white outline-none focus:border-[#D4FF00]";

function BodyEditor({ value, onChange }) {
  const textareaRef = useRef(null);
  const urlInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [insertOpen, setInsertOpen] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const openInsert = () => {
    setInsertOpen(true);
    setUrlDraft("");
    setTimeout(() => urlInputRef.current?.focus(), 30);
  };

  const insertAtCursor = (url) => {
    const ta = textareaRef.current;
    const pos = ta ? ta.selectionStart : (value || "").length;
    const before = (value || "").slice(0, pos);
    const after = (value || "").slice(pos);
    const needLeadNl = before.length > 0 && !before.endsWith("\n\n") ? (before.endsWith("\n") ? "\n" : "\n\n") : "";
    const needTrailNl = after.length > 0 && !after.startsWith("\n\n") ? (after.startsWith("\n") ? "\n" : "\n\n") : "\n\n";
    const insertion = `${needLeadNl}${url}${needTrailNl}`;
    const next = `${before}${insertion}${after}`;
    onChange(next);
    setTimeout(() => {
      if (ta) {
        const newPos = pos + insertion.length;
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      }
    }, 20);
  };

  const insertEmbed = () => {
    const url = urlDraft.trim();
    if (!url) return;
    insertAtCursor(url);
    setInsertOpen(false);
    setUrlDraft("");
  };

  const uploadAndEmbed = async (file) => {
    if (!file) return;
    setUploadError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Use the absolute URL so the published article renders from any origin.
      const url = resolveMediaUrl(res.data.url);
      insertAtCursor(url);
    } catch (e) {
      setUploadError(formatApiError(e.response?.data?.detail) || `Failed to upload ${file.name}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={openInsert}
          data-testid="body-insert-embed"
          className="flex items-center gap-2 border border-[#D4FF00] bg-[#D4FF00]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00] hover:bg-[#D4FF00] hover:text-black"
        >
          <PlusCircle size={12} weight="bold" /> Insert embed
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="body-upload-embed"
          className="flex items-center gap-2 border border-white/40 bg-black px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white hover:border-[#D4FF00] hover:text-[#D4FF00] disabled:opacity-50"
        >
          <UploadSimple size={12} weight="bold" /> {uploading ? "Uploading…" : "Upload & embed"}
        </button>
        <input
          ref={fileInputRef}
          data-testid="body-file-input"
          type="file"
          accept="image/*,video/*"
          className="hidden"
          onChange={(e) => uploadAndEmbed(e.target.files?.[0])}
        />
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Instagram · TikTok · YouTube · X · Vimeo · Image · Video</span>
      </div>

      {uploadError && <div data-testid="body-upload-error" className="mb-2 text-xs text-red-400">{uploadError}</div>}

      {insertOpen && (
        <div data-testid="embed-url-popover" className="mb-2 flex items-center gap-2 border border-[#D4FF00] bg-black p-2">
          <input
            ref={urlInputRef}
            data-testid="embed-url-input"
            type="url"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); insertEmbed(); }
              if (e.key === "Escape") setInsertOpen(false);
            }}
            placeholder="Paste URL (https://www.instagram.com/p/..., https://youtu.be/..., https://tiktok.com/...)"
            className="flex-1 bg-transparent px-2 py-1 text-sm text-white outline-none"
          />
          <button
            type="button"
            onClick={insertEmbed}
            data-testid="embed-url-confirm"
            className="flex items-center gap-1 bg-[#D4FF00] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-black hover:-translate-y-0.5"
          >
            <Check size={12} weight="bold" /> Insert
          </button>
          <button
            type="button"
            onClick={() => setInsertOpen(false)}
            className="border border-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 hover:border-white hover:text-white"
          >
            Cancel
          </button>
        </div>
      )}

      <textarea
        ref={textareaRef}
        data-testid="editor-body"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={12}
        className={`${inputCls} font-mono text-sm leading-relaxed`}
      />
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">{label}</div>
      {children}
    </label>
  );
}

const PRESET_CATEGORIES = ["ATP", "WTA", "News", "Podcast", "Grand Slams"];

function CategoryMultiSelect({ value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const [customInput, setCustomInput] = useState("");

  const toggle = (cat) => {
    const has = selected.includes(cat);
    onChange(has ? selected.filter((c) => c !== cat) : [...selected, cat]);
  };

  const addCustom = () => {
    const v = customInput.trim();
    if (!v) return;
    if (selected.includes(v)) { setCustomInput(""); return; }
    onChange([...selected, v]);
    setCustomInput("");
  };

  // Custom values not in preset list — keep them visible as removable chips.
  const customValues = selected.filter((c) => !PRESET_CATEGORIES.includes(c));

  return (
    <div data-testid="editor-categories">
      <div className="flex flex-wrap gap-2">
        {PRESET_CATEGORIES.map((cat) => {
          const active = selected.includes(cat);
          return (
            <button
              key={cat}
              type="button"
              data-testid={`category-toggle-${cat}`}
              onClick={() => toggle(cat)}
              className={`flex items-center gap-1 border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] transition-colors ${
                active ? "border-[#D4FF00] bg-[#D4FF00] text-black" : "border-white/15 text-white/70 hover:border-white"
              }`}
            >
              {active && <Check size={11} weight="bold" />}
              {cat}
            </button>
          );
        })}
      </div>

      {customValues.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {customValues.map((c) => (
            <span key={c} className="flex items-center gap-2 border border-[#D4FF00] bg-[#D4FF00]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00]">
              {c}
              <button type="button" onClick={() => toggle(c)} className="text-[#D4FF00]/70 hover:text-white">
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <input
          data-testid="category-custom-input"
          type="text"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          placeholder="Add a custom category…"
          className={`${inputCls} flex-1`}
        />
        <button
          type="button"
          onClick={addCustom}
          data-testid="category-custom-add"
          className="border border-white/15 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 hover:border-[#D4FF00] hover:text-[#D4FF00]"
        >
          <Plus size={12} weight="bold" />
        </button>
      </div>

      {selected.length === 0 && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/40">
          Pick one or more — posts will appear on every selected category page.
        </div>
      )}
    </div>
  );
}

function CoverImageField({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploadError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(resolveMediaUrl(res.data.url));
    } catch (e) {
      setUploadError(formatApiError(e.response?.data?.detail) || `Failed to upload ${file.name}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          data-testid="editor-cover"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} flex-1`}
          placeholder="Paste an image URL or upload one below"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="editor-cover-upload"
          className="flex items-center justify-center gap-2 border border-[#D4FF00] bg-[#D4FF00]/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00] hover:bg-[#D4FF00] hover:text-black disabled:opacity-50"
        >
          <UploadSimple size={12} weight="bold" /> {uploading ? "Uploading…" : "Upload image"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>

      {uploadError && <div className="mt-2 text-xs text-red-400">{uploadError}</div>}

      {value ? (
        <div className="mt-3 relative overflow-hidden border border-white/10 bg-black">
          <img
            src={resolveMediaUrl(value)}
            alt="Cover preview"
            data-testid="editor-cover-preview"
            className="aspect-[16/9] w-full object-cover"
          />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid="editor-cover-clear"
            className="absolute right-3 top-3 flex items-center gap-1 bg-black/80 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white hover:bg-red-600"
          >
            <X size={10} weight="bold" /> Replace
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          data-testid="editor-cover-dropzone"
          className={`mt-3 flex aspect-[16/9] cursor-pointer flex-col items-center justify-center border-2 border-dashed text-center transition-colors ${
            dragOver ? "border-[#D4FF00] bg-[#D4FF00]/5" : "border-white/20 bg-black/40 hover:border-white/40"
          }`}
        >
          <ImageIcon size={28} weight="bold" className="text-[#D4FF00]" />
          <div className="mt-3 font-display text-lg font-semibold tracking-tight text-white">
            {uploading ? "Uploading…" : "Drag & drop a cover image"}
          </div>
          <div className="mt-1 text-xs text-white/50">or click to browse — JPG, PNG, WEBP, GIF (max 60 MB)</div>
        </div>
      )}
    </div>
  );
}


function MediaUrlField({ type, value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const accept = type === "podcast"
    ? "audio/*,video/mp4,video/webm,video/quicktime"
    : type === "digital"
    ? "video/*"
    : "audio/*,video/*";
  const buttonLabel = type === "podcast" ? "Upload audio" : type === "digital" ? "Upload video" : "Upload media";

  const handleFile = async (file) => {
    if (!file) return;
    setUploadError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(resolveMediaUrl(res.data.url));
    } catch (e) {
      setUploadError(formatApiError(e.response?.data?.detail) || `Failed to upload ${file.name}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const isOurFile = !!value && (value.includes("/api/files/") || value.startsWith("/api/files/"));
  const isYouTube = !!value && /(youtube\.com|youtu\.be|vimeo\.com)/i.test(value);
  const isSpotify = !!value && /open\.spotify\.com\/(episode|show|playlist|track|album)\//i.test(value);
  const isAppleOrSoundCloud = !!value && /(podcasts\.apple\.com|soundcloud\.com)/i.test(value);
  const tikTokMatch = value && value.match(/tiktok\.com\/(?:@[^/]+\/video\/|v\/)(\d+)/);
  const isTikTok = !!tikTokMatch;

  const toSpotifyEmbed = (v) => {
    const m = v.match(/open\.spotify\.com\/(episode|show|playlist|track|album)\/([A-Za-z0-9]+)/);
    return m ? `https://open.spotify.com/embed/${m[1]}/${m[2]}?theme=0` : v;
  };

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          data-testid="editor-media"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} flex-1`}
          placeholder={type === "podcast" ? "Paste a Spotify / Apple / SoundCloud URL, or upload an audio file" : type === "digital" ? "Paste a YouTube / Vimeo / TikTok URL or upload below" : "https://..."}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="editor-media-upload"
          className="flex items-center justify-center gap-2 border border-[#D4FF00] bg-[#D4FF00]/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00] hover:bg-[#D4FF00] hover:text-black disabled:opacity-50"
        >
          <UploadSimple size={12} weight="bold" /> {uploading ? "Uploading…" : buttonLabel}
        </button>
        <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>

      {uploadError && <div className="mt-2 text-xs text-red-400">{uploadError}</div>}

      {value && (
        <div className="mt-3 border border-white/10 bg-black/40 p-3">
          {isSpotify ? (
            <iframe
              data-testid="editor-media-preview-spotify"
              src={toSpotifyEmbed(value)}
              title="Spotify preview"
              className="w-full border-0"
              style={{ height: /playlist|album/.test(value) ? 380 : 232 }}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            />
          ) : isAppleOrSoundCloud ? (
            <iframe
              data-testid="editor-media-preview-iframe"
              src={/soundcloud\.com/.test(value) ? `https://w.soundcloud.com/player/?url=${encodeURIComponent(value)}&color=%23E36A53` : value.replace("podcasts.apple.com", "embed.podcasts.apple.com")}
              title="Preview"
              className="h-[232px] w-full border-0"
            />
          ) : type === "podcast" || (type !== "digital" && /\.(mp3|wav|m4a|aac|flac|ogg|oga)(\?|$)/i.test(value)) ? (
            <audio data-testid="editor-media-preview-audio" src={value} controls className="w-full" />
          ) : isTikTok ? (
            <div className="aspect-[9/16] max-w-xs">
              <iframe
                data-testid="editor-media-preview-tiktok"
                src={`https://www.tiktok.com/embed/v2/${tikTokMatch[1]}`}
                title="TikTok preview"
                className="h-full w-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : isYouTube ? (
            <div className="aspect-video w-full">
              <iframe
                src={(() => {
                  const yt = value.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
                  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
                  const vm = value.match(/vimeo\.com\/(\d+)/);
                  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
                  return value;
                })()}
                title="Preview"
                className="h-full w-full"
                allowFullScreen
              />
            </div>
          ) : (
            <video data-testid="editor-media-preview-video" src={value} controls className="w-full" />
          )}
          {isOurFile && (
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#D4FF00]">Stored in object storage · ready to publish</div>
          )}
          {(isSpotify || isAppleOrSoundCloud) && (
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#D4FF00]">Embed ready · listeners can play in-page</div>
          )}
        </div>
      )}
    </div>
  );
}

function GalleryEditor({ value, onChange }) {
  // `value` is a newline-separated string (matches payload serializer).
  const urls = (value || "").split("\n").map((u) => u.trim()).filter(Boolean);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const inputRef = useRef(null);

  const appendUrls = (newUrls) => {
    const next = [...urls, ...newUrls].join("\n");
    onChange(next);
  };
  const removeAt = (idx) => {
    const next = urls.filter((_, i) => i !== idx).join("\n");
    onChange(next);
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadError("");
    setUploading(true);
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ current: i + 1, total: files.length, name: f.name });
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.data.url);
      } catch (e) {
        setUploadError(formatApiError(e.response?.data?.detail) || `Failed to upload ${f.name}`);
      }
    }
    if (uploaded.length) appendUrls(uploaded);
    setUploading(false);
    setProgress(null);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    await uploadFiles(e.dataTransfer.files);
  };

  const isImage = (u) => /\.(jpe?g|png|gif|webp|avif)(\?|$)/i.test(u);
  const isVideo = (u) => /\.(mp4|webm|mov|ogg)(\?|$)/i.test(u);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Gallery — images & videos in article</div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="gallery-browse"
          className="flex items-center gap-2 border border-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 hover:border-[#D4FF00] hover:text-[#D4FF00]"
        >
          <ImageIcon size={12} /> Browse files
        </button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="gallery-dropzone"
        className={`flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-8 text-center transition-colors ${
          dragging ? "border-[#D4FF00] bg-[#D4FF00]/5" : "border-white/20 bg-black/40 hover:border-white/40"
        }`}
      >
        <UploadSimple size={28} weight="bold" className="text-[#D4FF00]" />
        <div className="mt-3 font-display text-xl font-semibold tracking-tight text-white">
          {uploading ? "Uploading…" : "Drag & drop images or videos"}
        </div>
        <div className="mt-1 text-xs text-white/50">or click to browse — JPG, PNG, WEBP, GIF, MP4, WEBM, MOV (max 60 MB)</div>
        {uploading && progress && (
          <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#D4FF00]">
            {progress.current} / {progress.total} — {progress.name}
          </div>
        )}
        <input
          ref={inputRef}
          data-testid="gallery-file-input"
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
        />
      </div>

      {uploadError && <div data-testid="gallery-upload-error" className="mt-2 text-xs text-red-400">{uploadError}</div>}

      {urls.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
          {urls.map((u, i) => (
            <div key={`${i}:${u}`} className="group relative aspect-video overflow-hidden border border-white/10 bg-black">
              {isImage(u) ? (
                <img src={resolveMediaUrl(u)} alt="" className="h-full w-full object-cover" />
              ) : isVideo(u) ? (
                <video src={resolveMediaUrl(u)} className="h-full w-full object-cover" muted />
              ) : (
                <div className="flex h-full w-full items-center justify-center p-2 text-[10px] text-white/60">{u}</div>
              )}
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`gallery-remove-${i}`}
                className="absolute right-2 top-2 bg-black/80 p-1 text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100"
              >
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <details className="mt-3">
        <summary className="cursor-pointer text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-white">Or paste URLs manually (YouTube/Vimeo, external images)</summary>
        <textarea
          data-testid="editor-gallery"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={`${inputCls} mt-2`}
          placeholder={"https://www.youtube.com/embed/VIDEO_ID\nhttps://example.com/photo.jpg"}
        />
      </details>
    </div>
  );
}


function SubscribersPanel() {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/newsletter/subscribers"),
      api.get("/newsletter/stats"),
    ]).then(([s, st]) => {
      setSubs(s.data);
      setStats(st.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    const token = localStorage.getItem("ac_token");
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${BACKEND_URL}/api/newsletter/subscribers/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "baseline-breakdown-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const shown = subs.filter((s) => filter === "all" || s.status === filter);

  return (
    <div data-testid="subscribers-panel" className="mt-8">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-6">
        <div className="flex flex-wrap gap-6">
          <Stat label="Total" value={stats.total} />
          <Stat label="Active" value={stats.active} accent="#D4FF00" />
          <Stat label="Unsubscribed" value={stats.unsubscribed} />
        </div>
        <button
          onClick={downloadCsv}
          data-testid="subscribers-export"
          className="flex items-center gap-2 bg-[#D4FF00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black hover:-translate-y-0.5 disabled:opacity-50"
          disabled={subs.length === 0}
        >
          <DownloadSimple size={14} weight="bold" /> Export CSV
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {["all", "active", "unsubscribed"].map((s) => (
          <button
            key={s}
            data-testid={`subs-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`border px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
              filter === s ? "border-[#D4FF00] bg-[#D4FF00] text-black" : "border-white/15 text-white/70 hover:border-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-white/10">
        <div className="hidden grid-cols-[1fr_140px_140px_180px] gap-4 border-b border-white/10 bg-black px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 md:grid">
          <div>Email</div><div>Status</div><div>Source</div><div>Subscribed</div>
        </div>
        {loading && <div className="px-4 py-12 text-center text-xs uppercase tracking-[0.2em] text-white/40">Loading…</div>}
        {!loading && shown.map((s) => (
          <div key={s.id} className="grid grid-cols-1 gap-2 border-b border-white/10 px-4 py-3 text-sm md:grid-cols-[1fr_140px_140px_180px] md:items-center md:gap-4">
            <div className="flex items-center gap-2 text-white">
              <Envelope size={14} className="text-white/50" /> {s.email}
            </div>
            <div className={`text-xs ${s.status === "active" ? "text-[#D4FF00]" : "text-white/50"}`}>{s.status}</div>
            <div className="text-xs text-white/60">{s.source}</div>
            <div className="text-xs text-white/60">{new Date(s.subscribed_at).toLocaleString()}</div>
          </div>
        ))}
        {!loading && shown.length === 0 && (
          <div className="px-4 py-12 text-center text-xs uppercase tracking-[0.2em] text-white/40">
            No subscribers yet — share your site to start collecting emails.
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="mt-1 font-display text-3xl font-bold tracking-tighter" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

const CATEGORY_LABELS = {
  general: "General",
  story: "Story",
  podcast_guest: "Podcast Guest",
  partnership: "Partnership",
};

function MessagesPanel() {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/contact/messages"),
      api.get("/contact/stats"),
    ]).then(([m, s]) => {
      setMessages(m.data);
      setStats(s.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/contact/messages/${id}`, null, { params: { status } });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    await api.delete(`/contact/messages/${id}`);
    if (expanded === id) setExpanded(null);
    load();
  };

  const downloadCsv = async () => {
    const token = localStorage.getItem("ac_token");
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${BACKEND_URL}/api/contact/messages/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "baseline-breakdown-messages.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const shown = messages.filter((m) => filter === "all" || m.status === filter);

  return (
    <div data-testid="messages-panel" className="mt-8">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-6">
        <div className="flex flex-wrap gap-6">
          <Stat label="Total" value={stats.total} />
          <Stat label="Unread" value={stats.unread} accent="#D4FF00" />
        </div>
        <button
          onClick={downloadCsv}
          data-testid="messages-export"
          className="flex items-center gap-2 bg-[#D4FF00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black hover:-translate-y-0.5 disabled:opacity-50"
          disabled={messages.length === 0}
        >
          <DownloadSimple size={14} weight="bold" /> Export CSV
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {["all", "unread", "read", "archived"].map((s) => (
          <button
            key={s}
            data-testid={`messages-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`border px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
              filter === s ? "border-[#D4FF00] bg-[#D4FF00] text-black" : "border-white/15 text-white/70 hover:border-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-white/10">
        {loading && <div className="px-4 py-12 text-center text-xs uppercase tracking-[0.2em] text-white/40">Loading…</div>}
        {!loading && shown.map((m) => {
          const isOpen = expanded === m.id;
          return (
            <div key={m.id} data-testid={`message-row-${m.id}`} className="border-b border-white/10 last:border-b-0">
              <button
                type="button"
                onClick={() => {
                  setExpanded(isOpen ? null : m.id);
                  if (!isOpen && m.status === "unread") setStatus(m.id, "read");
                }}
                className="grid w-full grid-cols-1 gap-2 px-4 py-3 text-left transition-colors hover:bg-white/5 md:grid-cols-[14px_1fr_1.4fr_140px_180px] md:items-center md:gap-4"
              >
                <span className={`inline-block h-2 w-2 rounded-full ${m.status === "unread" ? "bg-[#D4FF00]" : "bg-white/20"}`} />
                <span className="flex items-center gap-2 text-sm text-white">
                  <ChatCircle size={14} className="text-white/50" />
                  <span className="truncate">{m.name} <span className="text-white/40">· {m.email}</span></span>
                </span>
                <span className="truncate text-sm text-white/80">{m.subject}</span>
                <span className="text-xs uppercase tracking-[0.15em] text-[#D4FF00]">{CATEGORY_LABELS[m.category] || m.category}</span>
                <span className="text-xs text-white/60">{new Date(m.created_at).toLocaleString()}</span>
              </button>
              {isOpen && (
                <div className="border-t border-white/10 bg-black/40 px-4 py-5">
                  <div className="whitespace-pre-wrap text-sm leading-relaxed text-white/85">{m.message}</div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <a
                      href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                      className="border border-[#D4FF00] bg-[#D4FF00]/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00] hover:bg-[#D4FF00] hover:text-black"
                    >
                      Reply via email
                    </a>
                    {m.status !== "archived" && (
                      <button
                        onClick={() => setStatus(m.id, "archived")}
                        className="border border-white/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 hover:border-white hover:text-white"
                      >
                        Archive
                      </button>
                    )}
                    {m.status === "archived" && (
                      <button
                        onClick={() => setStatus(m.id, "read")}
                        className="border border-white/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 hover:border-white hover:text-white"
                      >
                        Restore
                      </button>
                    )}
                    <button
                      onClick={() => remove(m.id)}
                      data-testid={`message-delete-${m.id}`}
                      className="ml-auto flex items-center gap-1 border border-red-500/40 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-red-300 hover:bg-red-500/10"
                    >
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!loading && shown.length === 0 && (
          <div className="px-4 py-12 text-center text-xs uppercase tracking-[0.2em] text-white/40">
            No messages yet — share your contact page to start the conversation.
          </div>
        )}
      </div>
    </div>
  );
}

function AccountPanel() {
  const { user, applySession } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // "ok" | null
  const [error, setError] = useState("");

  const emailChanged = newEmail && newEmail.toLowerCase() !== (user?.email || "").toLowerCase();
  const wantsPasswordChange = newPassword.length > 0 || confirmPassword.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus(null);

    if (!emailChanged && !wantsPasswordChange) {
      setError("Change the email or enter a new password.");
      return;
    }
    if (wantsPasswordChange) {
      if (newPassword.length < 8) {
        setError("New password must be at least 8 characters.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setError("New password and confirmation don't match.");
        return;
      }
    }
    if (!currentPassword) {
      setError("Enter your current password to confirm.");
      return;
    }

    setSaving(true);
    try {
      const body = { current_password: currentPassword };
      if (emailChanged) body.new_email = newEmail;
      if (wantsPasswordChange) body.new_password = newPassword;
      const res = await api.post("/auth/change-credentials", body);
      applySession(res.data);
      setStatus("ok");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not update credentials.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="account-panel" className="mt-8 max-w-xl">
      <div className="flex items-center gap-3 border-b border-white/10 pb-6">
        <div className="flex h-12 w-12 items-center justify-center border border-[#D4FF00] text-[#D4FF00]">
          <User size={22} weight="bold" />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4FF00]">Account</div>
          <div className="mt-1 font-display text-2xl font-semibold tracking-tight text-white">{user?.email}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/50">{user?.role}</div>
        </div>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <Field label="New Email">
          <input
            data-testid="account-new-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className={inputCls}
          />
        </Field>

        <div className="border-t border-white/10 pt-5">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">Change Password (optional)</div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="New Password">
              <input
                data-testid="account-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                className={inputCls}
              />
            </Field>
            <Field label="Confirm New Password">
              <input
                data-testid="account-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                className={inputCls}
              />
            </Field>
          </div>
        </div>

        <div className="border-t border-white/10 pt-5">
          <Field label="Current Password (required)">
            <input
              data-testid="account-current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              className={inputCls}
              required
            />
          </Field>
        </div>

        {status === "ok" && (
          <div data-testid="account-success" className="flex items-center gap-2 border border-[#D4FF00] bg-[#D4FF00]/5 px-4 py-3 text-sm text-[#D4FF00]">
            <Check size={16} weight="bold" /> Credentials updated. Use your new details next time you sign in.
          </div>
        )}
        {error && (
          <div data-testid="account-error" className="border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-300">{error}</div>
        )}

        <button
          type="submit"
          data-testid="account-save"
          disabled={saving}
          className="flex items-center gap-2 bg-[#D4FF00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black hover:-translate-y-0.5 disabled:opacity-50"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>
    </div>
  );
}

